/**
 * Local-first ticket template + event promoter storage.
 * Mirrors backend contracts in BACKEND.md until APIs ship.
 */
import type {
  EventPromoter,
  RsvpGuest,
  RsvpSettings,
  TicketTemplateConfig,
  TicketTemplateId,
} from "../types/rsvp";
import { DEFAULT_TICKET_TEMPLATE_CONFIG, normalizeGuestNumber } from "../types/rsvp";

const COMPANY_DEFAULT_KEY = "zoniq.ticketTemplate.companyDefault.v1";
const PROMOTERS_KEY = "zoniq.ticketTemplate.promoters.v1";
const GUESTS_KEY = "zoniq.rsvp.guests.v1";

type PromoterBag = Record<string, EventPromoter[]>;

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

function newId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `prom_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function getCompanyDefaultTemplateIdLocal(): TicketTemplateId {
  const raw = readJson<string>(COMPANY_DEFAULT_KEY, "black");
  return raw === "white" ? "white" : "black";
}

export function setCompanyDefaultTemplateIdLocal(id: TicketTemplateId): void {
  writeJson(COMPANY_DEFAULT_KEY, id);
}

export function mergeTemplateConfig(
  current?: TicketTemplateConfig | null,
  patch?: Partial<TicketTemplateConfig> | null,
): TicketTemplateConfig {
  return {
    ...DEFAULT_TICKET_TEMPLATE_CONFIG,
    ...current,
    ...patch,
  };
}

export function resolveTicketTemplateId(settings: RsvpSettings): TicketTemplateId {
  if (settings.resolved_ticket_template_id) return settings.resolved_ticket_template_id;
  if (settings.ticket_template_id) return settings.ticket_template_id;
  return getCompanyDefaultTemplateIdLocal();
}

export function withResolvedTemplateSettings(settings: RsvpSettings): RsvpSettings {
  const template_config = mergeTemplateConfig(settings.template_config);
  const resolved_ticket_template_id = resolveTicketTemplateId(settings);
  return {
    ...settings,
    template_config,
    resolved_ticket_template_id,
  };
}

function guestCountForPromoter(eventId: string, promoterId: string): number {
  const bag = readJson<Record<string, RsvpGuest[]>>(GUESTS_KEY, {});
  return (bag[eventId] ?? []).filter(
    (g) =>
      g.promoter_id === promoterId &&
      (g.channel === "guest_list" || g.source === "guest_list"),
  ).length;
}

export function listEventPromotersLocal(eventId: string): EventPromoter[] {
  const bag = readJson<PromoterBag>(PROMOTERS_KEY, {});
  return (bag[eventId] ?? [])
    .map((p) => ({
      ...p,
      guest_count: guestCountForPromoter(eventId, p.id),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function createEventPromoterLocal(eventId: string, name: string): EventPromoter {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Promoter name is required.");
  const bag = readJson<PromoterBag>(PROMOTERS_KEY, {});
  const list = bag[eventId] ?? [];
  const exists = list.some((p) => p.name.toLowerCase() === trimmed.toLowerCase());
  if (exists) throw new Error("A promoter with this name already exists.");
  const row: EventPromoter = {
    id: newId(),
    event_id: eventId,
    name: trimmed,
    created_at: new Date().toISOString(),
  };
  bag[eventId] = [...list, row];
  writeJson(PROMOTERS_KEY, bag);
  return { ...row, guest_count: 0 };
}

export function updateEventPromoterLocal(
  eventId: string,
  promoterId: string,
  name: string,
): EventPromoter {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Promoter name is required.");
  const bag = readJson<PromoterBag>(PROMOTERS_KEY, {});
  const list = bag[eventId] ?? [];
  const idx = list.findIndex((p) => p.id === promoterId);
  if (idx < 0) throw new Error("Promoter not found.");
  if (list.some((p, i) => i !== idx && p.name.toLowerCase() === trimmed.toLowerCase())) {
    throw new Error("A promoter with this name already exists.");
  }
  list[idx] = { ...list[idx], name: trimmed };
  bag[eventId] = list;
  writeJson(PROMOTERS_KEY, bag);
  return { ...list[idx], guest_count: guestCountForPromoter(eventId, promoterId) };
}

export function deleteEventPromoterLocal(eventId: string, promoterId: string): void {
  const bag = readJson<PromoterBag>(PROMOTERS_KEY, {});
  const list = bag[eventId] ?? [];
  const next = list.filter((p) => p.id !== promoterId);
  if (next.length === list.length) throw new Error("Promoter not found.");
  bag[eventId] = next;
  writeJson(PROMOTERS_KEY, bag);
}

export function findEventPromoterLocal(eventId: string, promoterId: string): EventPromoter | null {
  return listEventPromotersLocal(eventId).find((p) => p.id === promoterId) ?? null;
}

export function resolvePromoterName(eventId: string, promoterId?: string | null): string | null {
  if (!promoterId) return null;
  return findEventPromoterLocal(eventId, promoterId)?.name ?? null;
}

/** Build lineup rows from a single artist string (until backend lineup API ships). */
export function lineupFromArtist(artist?: string | null): { name: string; role?: string | null }[] {
  const name = artist?.trim();
  if (!name) return [];
  return [{ name, role: "headliner" }];
}

export function normalizePromoterPhoneKey(phone: string): string {
  return normalizeGuestNumber(phone);
}
